import React, { useState, useEffect } from 'react';
import {
    View,
    Text,
    StyleSheet,
    Animated,
} from 'react-native';
import { Hand, Sparkles } from 'lucide-react-native';
import { CandyIconButton } from './primitives/CandyIconButton';
import { studentTheme } from '../theme/tokens';

interface SilentHelpTokenProps {
    stage?: string;
    onRequestHelp: () => void;
    cooldownMs?: number;
    disabled?: boolean;
}

export const SilentHelpToken: React.FC<SilentHelpTokenProps> = ({
    stage = 'KS1',
    onRequestHelp,
    cooldownMs = 60000,
    disabled = false
}) => {
    const [cooldown, setCooldown] = useState(false);
    const [showReassurance, setShowReassurance] = useState(false);
    const [fadeAnim] = useState(new Animated.Value(0));

    const touchTarget = stage === 'EYFS' || stage === 'KS1' 
        ? studentTheme.touchTargets.ks1 
        : studentTheme.touchTargets.ks2;

    const handlePress = () => {
        if (cooldown || disabled) return;

        onRequestHelp();
        setCooldown(true);
        setShowReassurance(true);

        // Animate reassurance in
        Animated.sequence([
            Animated.timing(fadeAnim, {
                toValue: 1,
                duration: 300,
                useNativeDriver: true,
            }),
            Animated.delay(3500),
            Animated.timing(fadeAnim, {
                toValue: 0,
                duration: 400,
                useNativeDriver: true,
            })
        ]).start(() => {
            setShowReassurance(false);
        });
    };

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (cooldown) {
            timer = setTimeout(() => {
                setCooldown(false);
            }, cooldownMs);
        }
        return () => clearTimeout(timer);
    }, [cooldown, cooldownMs]);

    return (
        <View style={styles.container}>
            <CandyIconButton
                size={touchTarget}
                color={cooldown ? 'white' : 'yellow'}
                icon={Hand}
                onPress={handlePress}
                disabled={disabled || cooldown}
                accessibilityLabel="Ask teacher for a check-in quietly"
                accessibilityHint="Notifies the teacher discreetly without any sound or peer visibility"
            />

            {showReassurance && (
                <Animated.View
                    style={[
                        styles.reassurancePill,
                        { opacity: fadeAnim }
                    ]}
                    accessibilityLiveRegion="polite"
                >
                    <Sparkles size={16} color="#B45309" />
                    <Text style={styles.reassuranceText}>Teacher quietly notified ✨</Text>
                </Animated.View>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
    },
    reassurancePill: {
        position: 'absolute',
        top: '110%',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        backgroundColor: '#FEF3C7',
        borderColor: '#FDE68A',
        borderWidth: 1.5,
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 20,
        shadowColor: '#B45309',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.15,
        shadowRadius: 4,
        elevation: 3,
        zIndex: 100,
        minWidth: 180,
        justifyContent: 'center',
    },
    reassuranceText: {
        color: '#92400E',
        fontSize: 12,
        fontWeight: 'bold',
    }
});
